import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../../Ui_Components/PageHeader";
import StepIndicator from "../../../Team/AddNutritionist/components/StepIndicator";
import { ToastContainer, useToast } from "../../../Ui_Components/ToastMessage";
import { Button } from "../../../Ui_Components/Button";
import DetailsNutritionStep, {
  DuplicateNameInfo,
} from "./steps/DetailsNutritionStep";
import UnitConversionStep from "./steps/UnitConversionStep";
import DishCalculatorModal, {
  CalculatorIngredientRow,
  DishCalculatorApplyPayload,
} from "./DishCalculatorModal";
import type { DishDetail } from "../types";
import { mapApiToDishDetail } from "../dishApiMapper";
import {
  AddDishFormData,
  DetailsNutritionErrors,
  MICRO_NUTRIENT_BACKEND_KEY,
  MICRO_NUTRIENT_FIELDS,
  RecipeIngredientRow,
  UnitConversionErrors,
  buildFormFromDish,
  initialDetailsNutrition,
  initialMicroNutrients,
  initialUnitConversion,
  validateDetailsNutrition,
  validateUnitConversion,
} from "./types";
import {
  createDish,
  getDishDetails,
  updateDish,
} from "../../../../services/dishes";
import { getIngredientDetails } from "../../../../services/ingredients";
import { calculateCPFRatio } from "../../../Plans/Meal_Creation/CPFCalculator";

const STEPS = [
  { number: 1, label: "Details & nutrition" },
  { number: 2, label: "Unit conversion" },
];

// Block step-1 progression when the entered macros produce a negative
// component in the C:P:F ratio (fat + protein calories exceeding the stated
// total calories, which pushes carb % below zero). Returns null when the
// values are consistent or when calories haven't been entered yet.
const getNegativeCpfError = (details: {
  calories: string;
  adjProtein: string;
  fat: string;
  carbs: string;
}): string | null => {
  const cal = parseFloat(details.calories) || 0;
  if (cal <= 0) return null;
  const protein = parseFloat(details.adjProtein) || 0;
  const fat = parseFloat(details.fat) || 0;
  const carbs = parseFloat(details.carbs) || 0;
  const { ratio } = calculateCPFRatio(carbs, protein, fat, cal);
  if (Number(ratio.c) < 0 || Number(ratio.p) < 0 || Number(ratio.f) < 0) {
    return "C:P:F value cannot be negative. Fat and protein together exceed the total calories.";
  }
  return null;
};

const AddDishPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { toasts, removeToast, error: showError } = useToast();

  const isEditMode = Boolean(id);
  const [dishCalculatorOpen, setDishCalculatorOpen] = useState(false);
  // Warns the user that opening the calculator will overwrite the macros,
  // micros and ingredient rows they've already entered on this step.
  const [dishCalculatorConfirmOpen, setDishCalculatorConfirmOpen] =
    useState(false);
  // When entering edit from the View page we get the dish via navigation
  // state (no redundant fetch). Direct URL / refresh triggers a fetch
  // against GET_DISH_DETAILS below.
  const stateDish = (location.state as { dish?: DishDetail } | null)?.dish;
  const [editDish, setEditDish] = useState<DishDetail | undefined>(stateDish);
  const [loadingDish, setLoadingDish] = useState(isEditMode && !stateDish);
  const [dishLoadError, setDishLoadError] = useState<string | null>(null);

  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState<AddDishFormData>(() => {
    if (stateDish) return buildFormFromDish(stateDish);
    return {
      details: initialDetailsNutrition,
      conversion: initialUnitConversion,
    };
  });

  const [step1Errors, setStep1Errors] = useState<DetailsNutritionErrors>({});
  const [step2Errors, setStep2Errors] = useState<UnitConversionErrors>({});
  // Duplicate-name state bubbled up from Step 1. When set, "Save & next"
  // is blocked so the user can't submit an already-existing dish name.
  const [duplicateName, setDuplicateName] = useState<DuplicateNameInfo | null>(
    null,
  );

  // Fetch the dish from the API in edit mode when nav state didn't hand one
  // over (list "Edit" action, direct URL, or refresh).
  useEffect(() => {
    if (!isEditMode || !id || stateDish) return;
    let cancelled = false;
    setLoadingDish(true);
    setDishLoadError(null);
    (async () => {
      try {
        const api = await getDishDetails(id);
        if (cancelled) return;
        const isSuccess =
          api?.success === true ||
          api?.success === "1" ||
          api?.success === 1 ||
          api?.success === "true";
        if (!isSuccess || !api?.data) {
          setDishLoadError(api?.message || "Failed to load dish");
          setLoadingDish(false);
          return;
        }
        const mapped = mapApiToDishDetail(api.data);
        setEditDish(mapped);
        setFormData(buildFormFromDish(mapped));
        setLoadingDish(false);
      } catch (err) {
        if (cancelled) return;
        setDishLoadError(
          err instanceof Error ? err.message : "Failed to load dish",
        );
        setLoadingDish(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEditMode, stateDish]);

  // If the user navigates directly between edit URLs, or React Router reuses
  // the same instance across `/add` → `/:id/edit`, the initial useState
  // wouldn't re-run. Re-sync the form whenever the loaded dish id changes.
  useEffect(() => {
    if (editDish) {
      setFormData(buildFormFromDish(editDish));
      setCurrentStep(1);
      setStep1Errors({});
      setStep2Errors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDish?.id]);

  /* GET_DISH_DETAILS only ships each ingredient's picked-quantity nutrition
     — it doesn't include the measurements list or the per-100g baseline the
     Recipe & ingredients table's unit dropdown + the Dish calculator both
     need. After edit-mode hydration, look up each row's ingredient via
     GET_INGREDIENT_DETAILS (keyed by food_id) and fold measurements +
     base_quantity + per-100g nutrition into the row so the dropdown becomes
     interactive and the calculator can pre-populate from the current dish. */
  useEffect(() => {
    if (!editDish) return;
    // Read the food-id list off editDish (not formData) so this effect
    // doesn't race the sibling that repopulates formData in the same
    // commit cycle.
    const uniqueFoodIds = Array.from(
      new Set(
        editDish.ingredientList
          .map((row) => row.foodId)
          .filter((n): n is number => typeof n === "number" && n > 0),
      ),
    );
    if (uniqueFoodIds.length === 0) return;
    let cancelled = false;
    (async () => {
      const detailsByFoodId = new Map<
        number,
        {
          baseQuantity: number;
          measurements: { unit_type: string; measure_gram: number }[];
          nutritionPer100g: {
            cal: number;
            protein: number;
            grossProtein: number;
            fat: number;
            carb: number;
            netCarb: number;
            gl: number;
          };
        }
      >();
      await Promise.all(
        uniqueFoodIds.map(async (foodId) => {
          try {
            const api = await getIngredientDetails(foodId);
            const isSuccess =
              api?.success === true ||
              api?.success === "1" ||
              api?.success === 1 ||
              api?.success === "true";
            if (!isSuccess) return;
            const food = api?.food_data ?? {};
            const measurementsRaw: any[] = Array.isArray(food?.measurements)
              ? food.measurements
              : [];
            const measurements = measurementsRaw
              .map((m: any) => ({
                unit_type: String(
                  m?.measure_unit_type ?? m?.unit_type ?? "",
                ),
                measure_gram: Number(m?.measure_gram ?? 0) || 0,
              }))
              .filter((m) => m.unit_type);
            const baseQuantityNum = Number(food?.base_quantity ?? 0) || 100;
            const asNumber = (raw: unknown): number => {
              const n = typeof raw === "number" ? raw : parseFloat(String(raw ?? ""));
              return Number.isFinite(n) ? n : 0;
            };
            detailsByFoodId.set(foodId, {
              baseQuantity: baseQuantityNum,
              measurements,
              nutritionPer100g: {
                cal: asNumber(food?.cals),
                protein: asNumber(food?.AAFAdj_protein),
                grossProtein: asNumber(food?.gross_protein),
                fat: asNumber(food?.total_fat),
                carb: asNumber(food?.carbs),
                netCarb: asNumber(food?.["net carbs"] ?? food?.net_carbs),
                gl: asNumber(food?.GL),
              },
            });
          } catch (err) {
            console.error(
              "[AddDishPage] ingredient details fetch failed",
              foodId,
              err,
            );
          }
        }),
      );
      if (cancelled || detailsByFoodId.size === 0) return;
      setFormData((f) => ({
        ...f,
        details: {
          ...f.details,
          recipeIngredients: f.details.recipeIngredients.map((row) => {
            if (typeof row.foodId !== "number") return row;
            const meta = detailsByFoodId.get(row.foodId);
            if (!meta) return row;
            return {
              ...row,
              baseQuantity: meta.baseQuantity,
              measurements: meta.measurements,
              nutritionPer100g: meta.nutritionPer100g,
            };
          }),
        },
      }));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDish?.id]);

  const handleCancel = () => {
    if (isEditMode && id) navigate(`/food-library/dish/${id}`);
    else navigate("/food-library/dish");
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const apiType =
        formData.details.type === "veg"
          ? "VEG"
          : formData.details.type === "nonveg"
            ? "NON_VEG"
            : null;
      if (!apiType) throw new Error("Invalid diet type");

      // Skip locked base rows (gm/oz or ml/fl oz) — those are UI anchors the
      // backend already owns. Rows with blank unit or grams are also skipped.
      // Each row rides with the dish's base measure_type so the backend knows
      // whether the conversion is gram- or ml-based.
      const baseType = formData.details.unit;
      const unitPayload = formData.conversion.rows
        .filter((r) => !r.locked && r.unit && r.grams)
        .map((r) => ({
          measure_unit_type: r.unit,
          measure_gram: Number(r.grams),
          measure_type: baseType,
        }));

      // Cooking-fat bands only ride when the dish opts into dynamic fat.
      // Rows the user hasn't finished filling are skipped.
      const cookingFats = formData.details.cookingFatDynamic
        ? formData.details.cookingFatBands
            .filter((b) => b.minQty !== "" && b.maxQty !== "" && b.fat !== "")
            .map((b) => ({
              min_qty: Number(b.minQty),
              max_qty: Number(b.maxQty),
              cooking_fat_value: Number(b.fat),
            }))
        : undefined;

      // Recipe ingredients ride only when composed rows carry a food_id.
      // The recipe builder UI that assigns food_ids isn't wired yet, so
      // this stays empty for typical form submissions — leave the field
      // off entirely rather than sending an empty list.
      const ingredientsPayload = formData.details.recipeIngredients
        .filter(
          (row) =>
            typeof row.foodId === "number" &&
            row.quantity !== "" &&
            row.unit !== "",
        )
        .map((row) => ({
          food_id: row.foodId as number,
          quantity: Number(row.quantity),
          units: row.unit,
        }));

      // Micro-nutrients keyed by backend snake_case names. Empty fields pass
      // through as "" so the backend sees all 29 keys consistently.
      const microSource = formData.details.micro ?? initialMicroNutrients;
      const microPayload: Record<string, string> = {};
      MICRO_NUTRIENT_FIELDS.forEach((key) => {
        microPayload[MICRO_NUTRIENT_BACKEND_KEY[key]] = microSource[key] ?? "";
      });

      const payload = {
        dish_name: formData.details.name.trim(),
        dish_units: formData.details.unit,
        dish_base_quantity: Number(formData.details.baseQty),
        dish_gl: Number(formData.details.gl),
        dish_cals: Number(formData.details.calories),
        dish_AAFAdj_protein: Number(formData.details.adjProtein),
        dish_gross_protein: Number(formData.details.grossProtein),
        dish_carbs: formData.details.carbs
          ? Number(formData.details.carbs)
          : 0,
        net_carbs: Number(formData.details.netCarbs),
        category: formData.details.category,
        type: apiType,
        tags: formData.details.tags,
        fibre:
          formData.details.fiber !== ""
            ? Number(formData.details.fiber)
            : ("" as const),
        dish_ingredients_fat: Number(formData.details.fat),
        is_dynamic_fat: formData.details.cookingFatDynamic,
        units: unitPayload,
        cooking_fats: cookingFats,
        recipe_method: formData.details.recipeInstructions || undefined,
        ingredients:
          ingredientsPayload.length > 0 ? ingredientsPayload : undefined,
        micro: microPayload,
        dish_image: formData.details.photo,
      } as const;

      const api =
        isEditMode && id
          ? await updateDish({ ...payload, dish_id: id })
          : await createDish(payload);
      const isSuccess =
        api?.success === true ||
        api?.success === "1" ||
        api?.success === 1 ||
        api?.success === "true";
      if (!isSuccess) {
        throw new Error(
          api?.message ||
            (isEditMode ? "Failed to update dish" : "Failed to add dish"),
        );
      }

      // A fresh add lands on the list; a successful edit returns to the
      // View page (matches the ingredient flow).
      navigate(
        isEditMode && id ? `/food-library/dish/${id}` : "/food-library/dish",
        {
          state: {
            showSuccessToast: true,
            toastMessage:
              api?.message ||
              (isEditMode
                ? "Dish updated successfully"
                : "Dish added successfully"),
          },
        },
      );
    } catch (err) {
      showError(
        isEditMode ? "Failed to update dish" : "Failed to add dish",
        err instanceof Error ? err.message : undefined,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 1) {
      const errs = validateDetailsNutrition(formData.details);
      if (Object.keys(errs).length > 0) {
        setStep1Errors(errs);
        showError("Please fix the highlighted fields");
        return;
      }
      if (duplicateName) {
        showError(
          `${duplicateName.matchedItemName} already exists`,
          "Choose a different dish name to continue.",
        );
        return;
      }
      const cpfError = getNegativeCpfError(formData.details);
      if (cpfError) {
        showError("Invalid macro nutrients", cpfError);
        return;
      }
      setStep1Errors({});
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      const errs = validateUnitConversion(formData.conversion);
      if (errs.rows && Object.keys(errs.rows).length > 0) {
        setStep2Errors(errs);
        showError("Please fix the highlighted fields");
        return;
      }
      setStep2Errors({});
      handleSubmit();
    }
  };

  const handleOpenDishCalculator = () => setDishCalculatorConfirmOpen(true);
  const handleCloseDishCalculator = () => setDishCalculatorOpen(false);
  const handleConfirmOpenDishCalculator = () => {
    setDishCalculatorConfirmOpen(false);
    setDishCalculatorOpen(true);
  };
  const handleCancelOpenDishCalculator = () =>
    setDishCalculatorConfirmOpen(false);

  /* Reshape the form's recipe rows into the calculator's row shape so opening
     the calculator in edit mode pre-populates with the dish's current
     composition. Only rows that carry a food_id (i.e. picked from the admin
     ingredient search) are worth pre-populating — placeholder / empty rows
     are dropped so the calculator doesn't open with a blank noise row. When
     nothing usable exists, the calculator falls back to its default empty
     starter row. */
  const calculatorInitialRows: CalculatorIngredientRow[] | undefined =
    (() => {
      const usable = formData.details.recipeIngredients
        .filter((row) => typeof row.foodId === "number" && row.name)
        .map((row) => ({
          id: row.id,
          name: row.name,
          quantity: row.quantity,
          unit: row.unit,
          imageUrl: row.imageUrl,
          oil: row.oil,
          cal: row.cal,
          gl: row.gl,
          netCarb: row.netCarb,
          carb: row.carb,
          protein: row.aProt,
          grossProtein: row.grossProtein ?? "",
          fat: row.fats,
          cpf: row.cpf,
          foodId: row.foodId,
          baseQuantity: row.baseQuantity,
          measurements: row.measurements,
          nutritionPer100g: row.nutritionPer100g,
        }));
      return usable.length > 0 ? usable : undefined;
    })();
  // The base-quantity field on Step 1 already captures the dish's cooked
  // weight per serving; reuse it so the calculator's "Total cooked quantity"
  // strip opens with the right value in edit mode.
  const calculatorInitialCookedQty = isEditMode
    ? formData.details.baseQty
    : undefined;

  /* "Apply to dish" from the calculator flows two pieces of data back into
     the form:
       1. cookedTotals → the per-100g macros populate Card 2's nutrition
          fields (Calories / Adj protein / Gross protein / Carbs / Net
          carbs / Fat / GL). Users see the same numbers here as the
          "Total cooked quantity ≈ per 100 g" strip inside the calculator.
       2. rows → each composed ingredient becomes a Recipe & ingredients
          row with its per-row Cal / GL / carb / protein / fat + C:P:F.
     The calculator's row IDs are reused so re-opening the calculator
     later can be wired to hydrate from the current recipe (future work). */
  const handleApplyDishCalculator = (payload: DishCalculatorApplyPayload) => {
    // Rounded-integer for calories, 2dp for gram values. Applied values
    // always overwrite the form fields (including zeros) so re-opening
    // the calculator + applying is guaranteed to be a full replacement,
    // never a partial merge with whatever the user typed before.
    const formatMacroInt = (n: number): string =>
      Number.isFinite(n) ? String(Math.round(n)) : "0";
    const formatMacro = (n: number): string =>
      Number.isFinite(n) ? String(Number(n.toFixed(2))) : "0";

    const nextIngredients: RecipeIngredientRow[] = payload.rows.map((r) => ({
      id: r.id,
      name: r.name,
      imageUrl: r.imageUrl,
      quantity: r.quantity,
      unit: r.unit,
      cal: r.cal,
      carb: r.carb,
      netCarb: r.netCarb,
      fats: r.fat,
      aProt: r.protein,
      gl: r.gl,
      oil: r.oil,
      cpf: payload.rowCpf[r.id] ?? "",
      grossProtein: r.grossProtein,
      // Ingredient metadata — needed for the CREATE/UPDATE payload's
      // `ingredients` array AND so re-editing quantity/unit on the
      // Recipe & ingredients table can recompute nutrition without a
      // re-fetch.
      foodId: r.foodId,
      baseQuantity: r.baseQuantity,
      measurements: r.measurements,
      nutritionPer100g: r.nutritionPer100g,
    }));

    setFormData((f) => ({
      ...f,
      details: {
        ...f.details,
        calories: formatMacroInt(payload.cookedTotals.cal),
        adjProtein: formatMacro(payload.cookedTotals.protein),
        grossProtein: formatMacro(payload.cookedTotals.grossProtein),
        carbs: formatMacro(payload.cookedTotals.carb),
        netCarbs: formatMacro(payload.cookedTotals.netCarb),
        fat: formatMacro(payload.cookedTotals.fat),
        gl: formatMacro(payload.cookedTotals.gl),
        recipeIngredients:
          nextIngredients.length > 0
            ? nextIngredients
            : f.details.recipeIngredients,
      },
    }));

    // Clear any lingering step-1 errors that referenced the pre-apply
    // (empty) values so the "Save & next" button re-evaluates against
    // the freshly populated fields.
    setStep1Errors({});
    setDishCalculatorOpen(false);
  };

  const backButton = currentStep > 1 && (
    <button
      type="button"
      onClick={handleBack}
      disabled={submitting}
      className="flex items-center gap-2 text-[#0DB5B5] text-sm 3xl:text-base font-medium font-inter hover:text-[#0CA0A0] transition-colors disabled:opacity-50"
    >
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 19l-7-7 7-7"
        />
      </svg>
      Back
    </button>
  );

  const cancelButton = (
    <Button
      variant="secondary"
      size="md"
      onClick={handleCancel}
      disabled={submitting}
    >
      Cancel
    </Button>
  );

  const isStep1Complete =
    Object.keys(validateDetailsNutrition(formData.details)).length === 0 &&
    !duplicateName;
  const isStep2Complete = !validateUnitConversion(formData.conversion).rows;
  const currentStepComplete =
    currentStep === 1 ? isStep1Complete : isStep2Complete;
  const inactiveClass = !currentStepComplete
    ? "opacity-50 cursor-not-allowed"
    : "";

  const rightButton =
    currentStep === 2 ? (
      <Button
        variant="primary"
        size="md"
        onClick={handleNext}
        loading={submitting}
        className={inactiveClass}
      >
        {submitting
          ? isEditMode
            ? "Updating..."
            : "Saving..."
          : isEditMode
            ? "Update"
            : "Save"}
      </Button>
    ) : (
      <Button
        variant="primary"
        size="md"
        onClick={handleNext}
        className={inactiveClass}
      >
        Save &amp; next
      </Button>
    );

  return (
    <div className="h-screen flex flex-col bg-[#F7F7FA]">
      <PageHeader
        title={isEditMode ? "Edit dish" : "Add new dish"}
        breadcrumb={
          isEditMode
            ? `Dish > ${editDish?.name ?? "Edit dish"}`
            : "Dish > Add dish"
        }
        breadcrumbLinks={["/food-library/dish"]}
      />

      <StepIndicator currentStep={currentStep} steps={STEPS} />

      {loadingDish || dishLoadError ? (
        <div className="flex-1 flex items-center justify-center">
          <p
            className={`font-inter ${
              dishLoadError ? "text-red-500" : "text-gray-500"
            }`}
          >
            {loadingDish ? "Loading dish..." : dishLoadError}
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 pb-4 custom-scrollbar-overlay">
          <div className={currentStep === 1 ? "" : "hidden"}>
            <DetailsNutritionStep
              data={formData.details}
              onChange={(details) => {
                setFormData((f) => ({ ...f, details }));
                if (Object.keys(step1Errors).length > 0)
                  setStep1Errors(validateDetailsNutrition(details));
              }}
              errors={step1Errors}
              onError={showError}
              onOpenDishCalculator={handleOpenDishCalculator}
              originalName={editDish?.name}
              currentDishId={
                isEditMode && id !== undefined ? Number(id) : undefined
              }
              onDuplicateChange={setDuplicateName}
            />
          </div>
          <div className={currentStep === 2 ? "" : "hidden"}>
            <UnitConversionStep
              data={formData.conversion}
              unit={formData.details.unit}
              onChange={(conversion) => {
                setFormData((f) => ({ ...f, conversion }));
                if (step2Errors.rows && Object.keys(step2Errors.rows).length > 0)
                  setStep2Errors(validateUnitConversion(conversion));
              }}
              errors={step2Errors}
            />
          </div>
        </div>
      )}

      <div className="flex-shrink-0 bg-white border-t border-[#E8E8E8] px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-3">
        <div>{backButton}</div>
        <div className="flex items-center gap-3">
          {cancelButton}
          {rightButton}
        </div>
      </div>

      <DishCalculatorModal
        isOpen={dishCalculatorOpen}
        onClose={handleCloseDishCalculator}
        onApply={handleApplyDishCalculator}
        initialRows={calculatorInitialRows}
        initialCookedQuantity={calculatorInitialCookedQty}
      />

      {dishCalculatorConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-md w-full max-w-[560px] overflow-hidden">
            <div className="px-6 py-4 border-b-[0.5px] border-[#E5E5EA]">
              <h2 className="text-[18px] font-medium font-inter text-[#1F1F1F]">
                Switch to Dish Calculator?
              </h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm 3xl:text-base font-inter text-[#1F1F1F] leading-relaxed">
                If you continue to the{" "}
                <span className="font-semibold">Dish Calculator</span>, all the{" "}
                <span className="font-semibold">macronutrients</span>,{" "}
                <span className="font-semibold">micronutrients</span>, and{" "}
                <span className="font-semibold">ingredient</span> data you&apos;ve
                added will be replaced.
              </p>
            </div>
            <div className="px-6 py-4 border-t-[0.5px] border-[#E5E5EA] flex items-center justify-end gap-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCancelOpenDishCalculator}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirmOpenDishCalculator}
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default AddDishPage;
